// The longest post the server accepts (interact/post/serializers.py POST_MAX_LENGTH)
export const POST_MAX_LENGTH = 10000;

/** Shown under a post being written once it nears the limit, e.g. "9,620 / 10,000". */
export function lengthHint(text = '') {
  return text.length > POST_MAX_LENGTH * 0.9 ? `${text.length.toLocaleString()} / ${POST_MAX_LENGTH.toLocaleString()}` : '';
}

/** Posts are markdown written like messages: keep every line break (markdown would join single lines into
 *  one paragraph), except inside code blocks. */
export function withLineBreaks(text = '') {
  return text.split(/(```[\s\S]*?```)/).map((part, i) => (i % 2 ? part : part.replace(/([^\n])\n(?!\n)/g, '$1  \n'))).join('');
}

// "[text] (https://…)" and "![alt] (https://…)": a space slipped in before the address, which markdown would
// not take as a link or an image
const SPACED_LINK = /(!?\[[^\]\n]*\])[ \t]+\((https?:\/\/[^\s)]+)\)/g;

/** A post's text, ready for the markdown renderer. */
export function postMarkdown(text = '') {
  const fixed = text.split(/(```[\s\S]*?```)/).map((part, i) => (i % 2 ? part : part.replace(SPACED_LINK, '$1($2)'))).join('');
  return withLineBreaks(fixed);
}

/** The text without its markdown images: their addresses are not links to preview. */
export function withoutImages(text = '') {
  return text.replace(/!\[[^\]\n]*\][ \t]*\([^)\s]+\)/g, ' ');
}

/** A post's text without its markdown, for one- or two-line previews: images left out, a link's words kept,
 *  no heading, quote, list or emphasis marks, everything on one line. Hashtags stay. */
export function postPlainText(text = '') {
  return withoutImages(text)
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/`([^`]*)`/g, '$1')
    .replace(/\[([^\]\n]*)\][ \t]*\([^)\s]+\)/g, '$1')            // [words](address) -> words
    .replace(/^[ \t]{0,3}(#{1,6}[ \t]+|>[ \t]?|[-*+][ \t]+|\d+[.)][ \t]+)/gm, '')   // heading, quote, list marks
    .replace(/(\*\*|__)(.+?)\1/g, '$2')
    .replace(/(^|[^\w*])[*_]([^*_\n]+)[*_](?=[^\w*]|$)/g, '$1$2')
    .replace(/~~(.+?)~~/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
}

// The same rule the post renderer uses to show tags (LabMarkdown): # and a word with a letter, not "C#",
// "page#part", "#2024" or a link's "/#section"
const HASHTAG = /(^|[^\p{L}\p{N}_&/#])#([\p{L}\p{N}_]*\p{L}[\p{L}\p{N}_]*)/gu;

/** The hashtags in a post's text, without "#", once each, in order (code and addresses are not searched). */
export function hashtagsOf(text = '') {
  const plain = text
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/`[^`]*`/g, ' ')
    .replace(/\bhttps?:\/\/\S+/g, ' ');
  const seen = new Map();
  for (const m of plain.matchAll(HASHTAG)) {
    const key = m[2].toLowerCase();
    if (!seen.has(key)) seen.set(key, m[2]);
  }
  return [...seen.values()];
}

const IMAGE_FILE = /\.(png|jpe?g|gif|webp|avif|svg)(?:[?#]|$)/i;

/** The image a link preview stands for, when the link is an image itself (the server saw an image, or the
 *  address ends like one), else ''. Only https: the post shows it, like images written in the post. */
export function linkedImageOf(preview) {
  if (!preview?.url) return '';
  const src = preview.kind === 'image' ? (preview.image || preview.url)
    : IMAGE_FILE.test(preview.url.split('?')[0]) ? preview.url : '';
  return src.startsWith('https://') ? src : '';
}

/** Does this address look like an image file (for a preview built without the server)? */
export const looksLikeImage = (url = '') => IMAGE_FILE.test(url.split('?')[0]);

// Is the address at position I of TEXT bare (pasted as is), not part of markdown ([text](url), ![](url),
// <url>) or of `code`?
const bareAt = (text, i) => !/[(<]$/.test(text.slice(Math.max(0, i - 1), i))
  && (text.slice(0, i).match(/`/g) || []).length % 2 === 0;

// the positions of URL in TEXT
const positions = (text, url) => {
  const out = [];
  for (let i = text.indexOf(url); url && i >= 0; i = text.indexOf(url, i + url.length)) out.push(i);
  return out;
};

/** Is URL somewhere in TEXT as a bare address? */
export const isBareAddress = (text = '', url = '') => positions(text, url).some((i) => bareAt(text, i));

/** TEXT without the (first) bare address URL, its card standing for it; spaces tidied. */
export function removeAddress(text = '', url = '') {
  const i = positions(text, url).find((p) => bareAt(text, p));
  if (i === undefined) return text;
  return (text.slice(0, i) + text.slice(i + url.length))
    .replace(/[ \t]{2,}/g, ' ').replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
}

/** A post's text with the link its card stands for (a reflection made from the post keeps the link). */
export const withLink = (text = '', url = '') => (url && !text.includes(url) ? `${text}\n\n${url}`.trim() : text);
